import gsap from "gsap";
import AbstractComponent from "./abstract-component";

let SplitTypeLib = null;
async function getSplitType() {
    if (!SplitTypeLib) {
        const mod = await import(/* webpackChunkName: "split-type" */ "split-type");
        SplitTypeLib = mod.default || mod;
    }
    return SplitTypeLib;
}

export default class Reveal extends AbstractComponent {
    constructor(el) {
        super(el);
        this.isMobile = window.matchMedia('(max-width: 1024px), (pointer: coarse)').matches;
        this.isHome = document.body.classList.contains('home') || document.body.dataset.controller === 'home';
        this._started = false;
        this.DOM = {};
        this._splits = [];
        this._ioBlocks = null;
        this._ioTitles = null;
        this._onResize = null;
        this._onVisibilityChange = null;

        if (this.isMobile) return;

        this.$header = this.$el.closest(".c-header, .c-header-simple") || null;

        this.initDom();
        this.wireReveals();

        this._start = this._start.bind(this);
        this._maybeDeferToLoader();
    }

    initDom() {
        this.DOM.titlesSt2 = Array.from(this.$el.querySelectorAll(".st2"));
        this.DOM.titlesSt1 = Array.from(this.$el.querySelectorAll("h1"));
        this.DOM.revealBlocks = Array.from(this.$el.querySelectorAll("[data-reveal]"));

        if (this.$header) {
            this.DOM.desc = this.$header.querySelector(".c-header__description") || null;
            this.DOM.btn = this.$header.querySelector(".c-button") || null;
            this.DOM.titlesSt1.forEach(h1 => { h1.style.visibility = "hidden"; });
            if (this.DOM.desc) gsap.set(this.DOM.desc, { autoAlpha: 0, y: 16 });
            if (this.DOM.btn) gsap.set(this.DOM.btn, { autoAlpha: 0, y: 16 });
        } else {
            this.DOM.desc = null;
            this.DOM.btn = null;
        }
        // Hors header, on supprime les animations "au scroll": on ne cache rien.
        if (!this.$header) {
            this.DOM.titlesSt1.forEach((h1) => { h1.style.visibility = ""; });
            this.DOM.titlesSt2.forEach((t) => gsap.set(t, { clearProps: "all", visibility: "visible" }));
        } else if (!this.isHome && this.DOM.titlesSt2.length) {
            gsap.set(this.DOM.titlesSt2, { visibility: "hidden" });
        }
    }

    _maybeDeferToLoader() {
        const runAfterTransitionIfAny = () => {
            if (this._started) return;
            this._started = true;
          
            const run = () => {
              this._start();
              this.prepareSt2();
            };
          
            if (document.documentElement.classList.contains('is-transitioning')) {
              window.addEventListener('page-transition:done', run, { once: true });
            } else {
              run();
            }
          };

        const loader = document.querySelector('.c-site-loader');
        const shouldWaitLoader = !!loader && !loader.classList.contains('loaded');

        if (shouldWaitLoader) {
            window.addEventListener('site-loader:done', runAfterTransitionIfAny, { once: true });
        } else {
            runAfterTransitionIfAny();
        }
    }


    /* --------------------------- intro h1 + desc + bouton --------------------------- */
    async _start() {
        if (!this.$header) return;
        if (!this.DOM.titlesSt1.length) return;

        const SplitType = await getSplitType();
        const tl = gsap.timeline({ defaults: { ease: "power3.out" } });

        // H1
        this.DOM.titlesSt1.forEach((h1, idx) => {
            h1._splitInstance?.revert?.();
            const split = new SplitType(h1, { types: "words,chars", tagName: "span" });
            h1._splitInstance = split;
            this._splits.push(split);

            h1.style.visibility = "visible";
            gsap.set(split.chars, { yPercent: 120, opacity: 0 });

            tl.to(split.chars, {
                yPercent: 0,
                opacity: 1,
                duration: 0.7,
                stagger: 0.02
            }, idx === 0 ? 0 : "-=0.4");
        });

        if (this.DOM.desc) tl.to(this.DOM.desc, { autoAlpha: 1, y: 0, duration: 0.5 }, "+=0.15");
        if (this.DOM.btn) tl.to(this.DOM.btn, { autoAlpha: 1, y: 0, duration: 0.5 }, "-=0.25");

        this._bindResizeIfNeeded();
    }

    /* --------------------------- [data-reveal] generic --------------------------- */
    wireReveals() {
        if (!this.DOM.revealBlocks.length) return;

        // Sauf header de page: pas d'apparition au scroll, tout doit être visible immédiatement.
        if (!this.$header) {
            this.DOM.revealBlocks.forEach((el) => {
                gsap.set(el, { autoAlpha: 1, y: 0, clearProps: "transform" });
                const items = el.querySelectorAll("[data-reveal-item]");
                if (items?.length) gsap.set(items, { autoAlpha: 1, y: 0, clearProps: "transform" });
            });
            return;
        }

        this.DOM.revealBlocks.forEach((el) => gsap.set(el, { y: 0, autoAlpha: 0 }));

        this._ioBlocks = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    this.animateBlock(entry.target);
                    this._ioBlocks.unobserve(entry.target);
                }
            });
        }, { root: null, threshold: 0, rootMargin: "0px 0px -10% 0px" });

        this.DOM.revealBlocks.forEach((el) => this._ioBlocks.observe(el));
    }

    animateBlock(target) {
        const delay = parseFloat(target.getAttribute("data-reveal-delay") || "0") || 0;
        const stagger = parseFloat(target.getAttribute("data-reveal-stagger") || "0") || 0;
        const items = target.querySelectorAll("[data-reveal-item]");

        if (items.length) {
            gsap.to(target, { autoAlpha: 1, duration: 0.5, overwrite: true });
            gsap.set(items, { y: 40, autoAlpha: 0 });
            gsap.to(items, {
                y: 0, autoAlpha: 1, duration: 0.75, ease: "power3.out",
                stagger: stagger || 0.06, delay, overwrite: "auto"
            });
        } else {
            gsap.to(target, {
                y: 0, autoAlpha: 1, duration: 0.75, ease: "power3.out",
                delay, overwrite: "auto"
            });
        }
    }

    /* --------------------------- titres .st2 via IO --------------------------- */
    async prepareSt2() {
        if (!this.DOM.titlesSt2.length) return;

        // Sauf header de page: pas d'animation au scroll sur les titres.
        if (!this.$header) {
            this.DOM.titlesSt2.forEach((title) => {
                title.__split?.revert?.();
                title.__split = null;
                gsap.set(title, { clearProps: "all", visibility: "visible" });
            });
            return;
        }

        const SplitType = await getSplitType();
        this.DOM.titlesSt2.forEach(t => t.__split && t.__split.revert());

        this.DOM.titlesSt2.forEach((title) => {
            const split = new SplitType(title, { types: "words", lineClass: "split-line", wordClass: "split-word" });
            title.__split = split;
            this._splits.push(split);
            split.words.forEach((w) => gsap.set(w, { display: "inline-block", y: "1em", autoAlpha: 0 }));
        });

        if (!this._ioTitles) {
            this._ioTitles = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        this.animateTitle(entry.target);
                        this._ioTitles.unobserve(entry.target);
                    }
                });
            }, { root: null, threshold: 0, rootMargin: "0px 0px -30% 0px" });
        }
        this.DOM.titlesSt2.forEach((t) => this._ioTitles.observe(t));
        this._bindResizeIfNeeded();
    }

    animateTitle(title) {
        const split = title.__split;
        if (!split) return;
        gsap.set(title, { visibility: "visible" });
        gsap.to(split.words, {
            y: 0, autoAlpha: 1, duration: 0.7, ease: "power3.out",
            stagger: 0.02, overwrite: "auto"
        });
    }

    /* --------------------------- resize --------------------------- */
    _bindResizeIfNeeded() {
        if (this._onResize) return;
        this._onResize = () => {
            try {
                this._splits.forEach((s) => s.revert());
                this._splits = [];
                this.initDom();
                // Réinitialiser les h1 du header si présent
                if (this.$header && this.DOM.titlesSt1.length) {
                    this._start();
                }
                this.prepareSt2();
            } catch { }
        };
        window.addEventListener("resize", this._onResize, { passive: true });
        
        // Gérer le retour sur l'onglet
        this._onVisibilityChange = () => {
            if (!document.hidden && this.$header && this.DOM.titlesSt1.length) {
                // Vérifier si les éléments sont toujours cachés
                const isHidden = this.DOM.titlesSt1.some(h1 => h1.style.visibility === "hidden");
                if (isHidden) {
                    // Réinitialiser si nécessaire
                    try {
                        this._splits.forEach((s) => s.revert());
                        this._splits = [];
                        this.initDom();
                        this._start();
                        this.prepareSt2();
                    } catch { }
                }
            }
        };
        document.addEventListener("visibilitychange", this._onVisibilityChange);
    }

    /* --------------------------- destroy --------------------------- */
    destroy() {
        try {
            this._ioBlocks && this._ioBlocks.disconnect();
            this._ioTitles && this._ioTitles.disconnect();
            window.removeEventListener("resize", this._onResize);
            document.removeEventListener("visibilitychange", this._onVisibilityChange);
            this._splits.forEach((s) => s.revert());
            this._splits = [];
        } catch { }

        this.DOM.titlesSt1?.forEach((h1) => {
            h1._splitInstance?.revert?.();
            h1._splitInstance = null;
            h1.style.visibility = "";
        });
        if (this.DOM.desc) gsap.set(this.DOM.desc, { clearProps: "all" });
        if (this.DOM.btn) gsap.set(this.DOM.btn, { clearProps: "all" });
        this.DOM.titlesSt2?.forEach(t => t.classList.remove("is-reveal-ready"));
        this.DOM.titlesSt1?.forEach(t => t.classList.remove("is-reveal-ready"));
    }
}
